import {createSlice} from '@reduxjs/toolkit';
import type {FolderData} from './components/folders-list/folders-list.types';
import type {DesignData} from './components/design-grid/design-grid.types';
import {fetchFolders, fetchDesigns, fetchFolderContents, fetchSearchResults} from './poster-design-panel-thunk';

export const ROOT_VIEW_KEY = 'root';
export const SHARED_ROOT_VIEW_KEY = 'shared-root';

export interface ViewCache {
  folders: FolderData[];
  foldersLoading: boolean;
  foldersLoaded: boolean;
  foldersHasError: boolean;

  designs: DesignData[];
  designsOffset: number;
  designsHasMore: boolean;
  designsLoading: boolean;
  designsLoaded: boolean;
  designsHasError: boolean;
}

export interface SearchCache {
  folders: FolderData[];
  designs: DesignData[];
  designsOffset: number;
  designsHasMore: boolean;
  designsLoading: boolean;
  designsLoaded: boolean;
  designsHasError: boolean;
}

export interface PosterDesignPanelState {
  views: Record<string, ViewCache>;
  searchResults: Record<string, SearchCache>;
}

export enum SearchScope {
  SHARED = 'shared',
  MINE = 'mine',
}

export function getSearchKey(searchTerm: string, isShared: boolean): string {
  return `${isShared ? SearchScope.SHARED : SearchScope.MINE}:${searchTerm}`;
}

const EMPTY_VIEW_CACHE: ViewCache = {
  folders: [],
  foldersLoading: false,
  foldersLoaded: false,
  foldersHasError: false,

  designs: [],
  designsOffset: 0,
  designsHasMore: false,
  designsLoading: false,
  designsLoaded: false,
  designsHasError: false,
};

const EMPTY_SEARCH_CACHE: SearchCache = {
  folders: [],
  designs: [],
  designsOffset: 0,
  designsHasMore: false,
  designsLoading: false,
  designsLoaded: false,
  designsHasError: false,
};

const initialState: PosterDesignPanelState = {views: {}, searchResults: {}};

function ensureView(state: PosterDesignPanelState, viewKey: string): void {
  state.views[viewKey] ??= {...EMPTY_VIEW_CACHE};
}

function ensureSearchCache(state: PosterDesignPanelState, searchKey: string): void {
  state.searchResults[searchKey] ??= {...EMPTY_SEARCH_CACHE, folders: [], designs: []};
}

const posterDesignPanelSlice = createSlice({
  name: 'posterDesignPanel',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFolders.pending, (state, action) => {
        const {viewKey} = action.meta.arg;
        ensureView(state, viewKey);
        state.views[viewKey].foldersLoading = true;
        state.views[viewKey].foldersHasError = false;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        const {viewKey, folders} = action.payload;
        ensureView(state, viewKey);
        const view = state.views[viewKey];
        view.folders = folders;
        view.foldersLoading = false;
        view.foldersLoaded = true;
        view.foldersHasError = false;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        const {viewKey} = action.meta.arg;
        if (state.views[viewKey] !== undefined) {
          state.views[viewKey].foldersLoading = false;
          state.views[viewKey].foldersHasError = true;
        }
      })
      .addCase(fetchDesigns.pending, (state, action) => {
        const {viewKey} = action.meta.arg;
        ensureView(state, viewKey);
        state.views[viewKey].designsLoading = true;
        state.views[viewKey].designsHasError = false;
      })
      .addCase(fetchDesigns.fulfilled, (state, action) => {
        const {viewKey, designs, more, nextOffset} = action.payload;
        ensureView(state, viewKey);
        const view = state.views[viewKey];
        view.designs.push(...designs);
        view.designsOffset = nextOffset;
        view.designsHasMore = more;
        view.designsLoading = false;
        view.designsLoaded = true;
        view.designsHasError = false;
      })
      .addCase(fetchDesigns.rejected, (state, action) => {
        const {viewKey} = action.meta.arg;
        if (state.views[viewKey] !== undefined) {
          state.views[viewKey].designsLoading = false;
          state.views[viewKey].designsHasError = true;
        }
      })
      .addCase(fetchFolderContents.pending, (state, action) => {
        const {viewKey} = action.meta.arg;
        ensureView(state, viewKey);
        state.views[viewKey].foldersLoading = true;
        state.views[viewKey].designsLoading = true;
        state.views[viewKey].foldersHasError = false;
        state.views[viewKey].designsHasError = false;
      })
      .addCase(fetchFolderContents.fulfilled, (state, action) => {
        const {viewKey, folders, designs, more, nextDesignsOffset} = action.payload;
        ensureView(state, viewKey);
        const view = state.views[viewKey];
        view.folders = folders;
        view.foldersLoading = false;
        view.foldersLoaded = true;
        view.foldersHasError = false;
        view.designs = designs;
        view.designsOffset = nextDesignsOffset;
        view.designsHasMore = more;
        view.designsLoading = false;
        view.designsLoaded = true;
        view.designsHasError = false;
      })
      .addCase(fetchFolderContents.rejected, (state, action) => {
        const {viewKey} = action.meta.arg;
        if (state.views[viewKey] !== undefined) {
          state.views[viewKey].foldersLoading = false;
          state.views[viewKey].designsLoading = false;
          state.views[viewKey].foldersHasError = true;
          state.views[viewKey].designsHasError = true;
        }
      })
      .addCase(fetchSearchResults.pending, (state, action) => {
        const {searchKey} = action.meta.arg;
        ensureSearchCache(state, searchKey);
        state.searchResults[searchKey].designsLoading = true;
        state.searchResults[searchKey].designsHasError = false;
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        const {searchKey, isFirstPage, folders, designs, more, nextOffset} = action.payload;
        ensureSearchCache(state, searchKey);
        const cache = state.searchResults[searchKey];
        if (isFirstPage) {
          cache.folders = folders;
          cache.designs = designs;
        } else {
          cache.designs.push(...designs);
        }
        cache.designsOffset = nextOffset;
        cache.designsHasMore = more;
        cache.designsLoading = false;
        cache.designsLoaded = true;
        cache.designsHasError = false;
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        const {searchKey} = action.meta.arg;
        if (state.searchResults[searchKey] !== undefined) {
          state.searchResults[searchKey].designsLoading = false;
          state.searchResults[searchKey].designsHasError = true;
        }
      });
  },
});

export const posterDesignPanelReducer = posterDesignPanelSlice.reducer;

export const selectViewCache = (
  state: {posterDesignPanel: PosterDesignPanelState},
  viewKey: string,
): ViewCache | undefined => state.posterDesignPanel.views[viewKey];

export const selectSearchCache = (
  state: {posterDesignPanel: PosterDesignPanelState},
  searchKey: string,
): SearchCache | undefined => state.posterDesignPanel.searchResults[searchKey];
